import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledTooltip,
} from "reactstrap";
import { Link } from "react-router-dom";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchEmployeeDropdownAutoList, fetchEmployeeResignation, fetchEmployees, saveEmployeeRejoin, saveEmployeeResignation } from "store/actions";
import CustomDatePicker from "common/CustomDatePicker";
import CustomSelect from "components/Common/custom-select";
import { convertToInputDate } from "common/dateFormat/formatDate";
import Loader from "common/pageLoader";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useLocation,useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import useButtonAccess from "components/Common/useButtonAccess";

const EmployeeResignation = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const rights = getAccessRights(location.pathname, history);
    setAccessRight(rights?.data);
  }, [location]);
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    resignationList,
    dropdownAutoList,
    employees,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    resignationList: state.EmployeeResignationReducer?.resignationList ?? [],
    employees: state.EmployeeDetailsReducer?.employees ?? [],
    dropdownAutoList: state.EmployeeDetailsReducer?.dropdownAutoList ?? [],
    loading: state.EmployeeResignationReducer?.loading || state.EmployeeDetailsReducer?.loading,
    error: state.EmployeeResignationReducer?.error || state.EmployeeDetailsReducer?.error,
    success: state.EmployeeResignationReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id
    dispatch(fetchEmployeeResignation(unitId));
    dispatch(fetchEmployeeDropdownAutoList(unitId))
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
      setRejoinId(null);
      dispatch(fetchEmployeeResignation(authDetails?.unit_id));
    }
  }, [authDetails, success, dispatch]);


  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [rejoinId, setRejoinId] = useState(null);
  const [isRejoinModalOpen, setIsRejoinModalOpen] = useState(false);
  const [rejoinReason, setRejoinReason] = useState("");

  const toggleRejoinModal = () => setIsRejoinModalOpen(!isRejoinModalOpen);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      employee: "",
      status: "",
      resignedDate: "",
      acceptedDate: "",
      npDays: '',
      expectedRelieveDate: "",
      relieveDate: "",
      reason: "",
    },
    validationSchema: Yup.object({
      employee: Yup.object()
        .nullable()
        .required("Employee is required"),

      status: Yup.string()
        .nullable()
        .required("Status is required"),

      resignedDate: Yup.date()
        .nullable()
        .required("Resigned Date is required")
        .typeError("Enter a valid date"),

      acceptedDate: Yup.date()
        .nullable()
        .required("Accepted Date is required")
        .typeError("Enter a valid date"),

      npDays: Yup.number()
        .nullable()
        .required("Notice Period is required")
        .integer("Notice Period must be an integer")
        .min(0, "Notice Period cannot be negative 0")
        .max(999, "Notice Period must not exceed 3 digits"),

      expectedRelieveDate: Yup.date()
        .nullable()
        .required("Expected Relieve Date is required")
        .typeError("Enter a valid date"),

      relieveDate: Yup.date()
        .nullable()
        .required("Relieve Date is required")
        .typeError("Enter a valid date"),

      reason: Yup.string()
        .nullable()
        .required("Reason is required").max(200, "Reason must not exceed 200 characters"),
    }),

    onSubmit: (values, { resetForm }) => {
      const formDataObj = new FormData();
      formDataObj.append("emp_id", values.employee?.value);
      formDataObj.append("emp_status", values.status === "Active" ? 1 : 0);
      formDataObj.append("resign_dt", values.resignedDate);
      formDataObj.append("accept_dt", values.acceptedDate);
      formDataObj.append("notice_period", String(values.npDays));
      formDataObj.append("expectrelieve_dt", values.expectedRelieveDate);
      formDataObj.append("relieve_dt", values.relieveDate);
      formDataObj.append("reason", values.reason);
      formDataObj.append("create_by", authDetails?.user_id);
      dispatch(saveEmployeeResignation(formDataObj))
    },
  });

  const handleEdit = (row) => {
    setIsEdit(true);
    setEditId(row.id);
    setRejoinId(null); // Ensure rejoin state is cleared

    formik.setValues({
      employee: {
        value: row.emp_id,
        label: row.emp_name,
      },
      status: row.emp_status === "Active" ? "Active" : "Inactive",
      resignedDate: convertToInputDate(row.resign_dt),
      acceptedDate: convertToInputDate(row.accept_dt),
      npDays: row.notice_period,
      expectedRelieveDate: convertToInputDate(row.expectrelieve_dt),
      relieveDate: convertToInputDate(row.relieve_dt),
      reason: row.reason || "",
    });
  };

  const handleRejoin = (row) => {
    setRejoinId(row.emp_id);
    setRejoinReason(""); // Clear any previous rejoin reason
    toggleRejoinModal(); // Open the modal
  };


  const handleRejoinSubmit = () => {
    const formDataObj = new FormData();
    formDataObj.append("emp_id", rejoinId);
    formDataObj.append("reason", rejoinReason);
    formDataObj.append("create_by", authDetails?.user_id);
    dispatch(saveEmployeeRejoin(formDataObj))
    // Close the modal and reset the rejoin state
    toggleRejoinModal();
    setRejoinId(null); // Clear the rejoinId to enable the form fields
  };

  // const handleCancel = () => {
  //   formik.resetForm(); // Reset form fields
  //   setIsEdit(false);
  //   setEditId(null);
  //   setRejoinId(null); // Clear rejoinId to prevent form fields from being disabled
  // };

  const handleCancel = async (e) => {
    e.preventDefault();

    // Optional: if nothing changed, just reset silently
    if (!formik.dirty && !isEdit && !rejoinId) {
      formik.resetForm();
      return;
    }

    const result = await Swal.fire({
      title: "Confirm Cancel",
      text: "All entered data will be cleared. Do you want to continue?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      confirmButtonColor: "rgb(145, 73, 234)",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    // ✅ Reset Formik
    formik.resetForm({
      values: {
        employee: null,
        status: "",
        resignedDate: "",
        acceptedDate: "",
        npDays: "",
        expectedRelieveDate: "",
        relieveDate: "",
        reason: "",
      },
    });

    // ✅ Reset page states
    setIsEdit(false);
    setEditId(null);
    setRejoinId(null);
  };

  const columns = [
    ...(canEdit ? [
      {
        Header: "Action",
        className: "text-center",
        Cell: ({ row }) => {
          const emp = row.original;
          const empId = emp?.emp_id;

          return (
            <div className="d-flex gap-3 justify-content-center">

              {/* Edit */}
              {canEdit && (
                <Link
                  to="#"
                  className="text-success"
                  onClick={() => handleEdit(emp)}
                >
                  <i className="bx bxs-edit font-size-18" id={`edittool-${empId}`} />
                  <UncontrolledTooltip target={`edittool-${empId}`}>
                    Edit Employee
                  </UncontrolledTooltip>
                </Link>
              )}

              {/* Rejoin */}
              {canEdit && (
                <Link
                  to="#"
                  className="text-info"
                  onClick={() => handleRejoin(emp)}
                >
                  <i className="mdi mdi-account-arrow-left font-size-18" id={`rejoin-${empId}`} />
                  <UncontrolledTooltip target={`rejoin-${empId}`}>
                    Rejoin Employee
                  </UncontrolledTooltip>
                </Link>
              )}
            </div>
          );
        },
      }
    ] : []),
    { Header: "S.No", className: "serial-no",applyHeaderClass: true, Cell: ({ row }) => resignationList.length - row.index },
    // {
    //   Header: "Action",
    //   Cell: (cellProps) => {
    //     const empId = cellProps.row.original?.emp_id;
    //     return (
    //       <div className="d-flex gap-3">
    //         {/* Edit icon with tooltip */}
    //         <Link
    //           to="#"
    //           className="text-success"
    //           onClick={() => handleEdit(cellProps.row.original)}
    //         >
    //           <i
    //             className="bx bxs-edit font-size-18"
    //             id={`edittooltip-${empId}`}
    //           />
    //           <UncontrolledTooltip placement="top" target={`edittooltip-${empId}`}>
    //             Edit Employee
    //           </UncontrolledTooltip>
    //         </Link>

    //         {/* Rejoin icon with tooltip */}
    //         <Link
    //           to="#"
    //           className="text-info"
    //           onClick={() => handleRejoin(cellProps.row.original)}
    //         >
    //           <i
    //             className="mdi mdi-account-arrow-left font-size-18"
    //             id={`rejointooltip-${empId}`}
    //           />
    //           <UncontrolledTooltip placement="top" target={`rejointooltip-${empId}`}>
    //             Rejoin Employee
    //           </UncontrolledTooltip>
    //         </Link>
    //       </div>
    //     );
    //   },
    // },
    { Header: "Code", accessor: "emp_no" },
    { Header: "Name", accessor: "emp_name" },
    { Header: "Status", accessor: "emp_status" },
    { Header: "Resigned", accessor: "resign_dt" },
    { Header: "Accepted", accessor: "accept_dt" },
    { Header: "NP Days", accessor: "notice_period" },
    { Header: "Expected", accessor: "expectrelieve_dt" },
    { Header: "Relieved", accessor: "relieve_dt" },
    { Header: "Reason", accessor: "reason" },
    { Header: "Created By", accessor: "created_by" },
    { Header: "Created On", accessor: "created_dt" },    
  ];

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  useEffect(() => {
    const accepted = formik.values.acceptedDate;
    const npDays = parseInt(formik.values.npDays);

    if (accepted && !isNaN(npDays)) {
      const calculated = dayjs(accepted).add(npDays, "day").format("YYYY-MM-DD");
      formik.setFieldValue("expectedRelieveDate", calculated);
    }
  }, [formik.values.acceptedDate, formik.values.npDays]);


  return (
    <React.Fragment>
      <div className="page-content">
        <Loader loading={loading} />
        <Container fluid>
          <Breadcrumbs title="Employee" breadcrumbItem="Employee Resignation" />

          {/* Input Section */}
          <Card>
            <CardBody>
              <Form onSubmit={formik.handleSubmit}>
                <Row className="mb-3 g-md-2 g-lg-2 g-xl-3">
                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="employee">Employee <span className="text-danger">*</span></Label>
                      <CustomSelect
                        id="employee"
                        name="employee"
                        options={dropdownAutoList.map(emp => ({ value: emp.emp_id, label: emp.emp_no }))}
                        value={formik.values.employee || null}  // Store full object
                        onChange={(selectedOption) => formik.setFieldValue("employee", selectedOption)}
                      />
                      {formik.touched.employee && formik.errors.employee && (
                        <div className="text-danger">{formik.errors.employee}</div>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="status">Status <span className="text-danger">*</span></Label>
                      <CustomSelect
                        id="empstatus"
                        name="status"
                        options={statusOptions}
                        value={statusOptions.find(
                          (opt) => opt.value === formik.values.status
                        )}
                        onChange={(selectedOption) =>
                          formik.setFieldValue(
                            "status",
                            selectedOption ? selectedOption.value : ""
                          )
                        }
                        onBlur={formik.handleBlur}
                        isdisabled={rejoinId !== null} // Added here
                        className={
                          formik.touched.status && formik.errors.status ? "is-invalid" : ""
                        }
                      />
                      {formik.touched.status && formik.errors.status && (
                        <div className="text-danger">{formik.errors.status}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="resignedDate">Resigned Date<span className="text-danger">*</span></Label>
                      <CustomDatePicker
                        value={formik.values.resignedDate}
                        onChange={(date) => formik.setFieldValue("resignedDate", date)}
                        onBlur={() => formik.setFieldTouched("resignedDate", true)}
                      />
                      {formik.touched.resignedDate && formik.errors.resignedDate && (
                        <div className="text-danger">{formik.errors.resignedDate}</div>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="acceptedDate">Accepted Date<span className="text-danger">*</span></Label>
                      <CustomDatePicker
                        value={formik.values.acceptedDate}
                        onChange={(date) => formik.setFieldValue("acceptedDate", date)}
                        onBlur={() => formik.setFieldTouched("acceptedDate", true)}
                      />
                      {formik.touched.acceptedDate && formik.errors.acceptedDate && (
                        <div className="text-danger">{formik.errors.acceptedDate}</div>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="npDays">Notice Period (Days)<span className="text-danger">*</span></Label>
                      <Input
                        id="npDays"
                        name="npDays"
                        type="number"
                        value={formik.values.npDays}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={rejoinId !== null} // Added here
                        className={formik.touched.npDays && formik.errors.npDays ? "is-invalid" : ""}
                      />
                      {formik.touched.npDays && formik.errors.npDays && (
                        <div className="text-danger">{formik.errors.npDays}</div>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="expectedRelieveDate">Expected Relieve Date<span className="text-danger">*</span></Label>
                      <CustomDatePicker
                        value={formik.values.expectedRelieveDate}
                        onChange={(date) => formik.setFieldValue("expectedRelieveDate", date)}
                        onBlur={() => formik.setFieldTouched("expectedRelieveDate", true)}
                      />
                      {formik.touched.expectedRelieveDate &&
                        formik.errors.expectedRelieveDate && (
                          <div className="text-danger">{formik.errors.expectedRelieveDate}</div>
                        )}
                    </FormGroup>
                  </Col>

                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="relieveDate">Relieve Date<span className="text-danger">*</span></Label>
                      <CustomDatePicker
                        value={formik.values.relieveDate}
                        onChange={(date) => formik.setFieldValue("relieveDate", date)}
                        onBlur={() => formik.setFieldTouched("relieveDate", true)}
                      />
                      {formik.touched.relieveDate && formik.errors.relieveDate && (
                        <div className="text-danger">{formik.errors.relieveDate}</div>
                      )}
                    </FormGroup>
                  </Col> */}

                  {/* Resigned Date */}
                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="resignedDate">
                        Resigned Date <span className="text-danger">*</span>
                      </Label>
                      <CustomDatePicker
                        name="resignedDate"
                        value={formik.values.resignedDate}
                        onChange={(date) => formik.setFieldValue("resignedDate", date)}
                        onBlur={() => formik.setFieldTouched("resignedDate", true)}
                      />
                      {formik.touched.resignedDate && formik.errors.resignedDate && (
                        <div className="text-danger">{formik.errors.resignedDate}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Accepted Date */}
                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="acceptedDate">
                        Accepted Date <span className="text-danger">*</span>
                      </Label>
                      <CustomDatePicker
                        name="acceptedDate"
                        value={formik.values.acceptedDate}
                        onChange={(date) => formik.setFieldValue("acceptedDate", date)}
                        disablePast={true}
                        onBlur={() => formik.setFieldTouched("acceptedDate", true)}
                        minDate={formik.values.resignedDate || null}
                        maxDate={formik.values.expectedRelieveDate || null}
                      />
                      {formik.touched.acceptedDate && formik.errors.acceptedDate && (
                        <div className="text-danger">{formik.errors.acceptedDate}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Notice Period */}
                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="npDays">
                        Notice Period (Days) <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="npDays"
                        name="npDays"
                        type="number"
                        maxLength={3}
                        value={formik.values.npDays}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={rejoinId !== null}
                        // className={formik.touched.npDays && formik.errors.npDays ? "is-invalid" : ""}
                      />
                      {formik.touched.npDays && formik.errors.npDays && (
                        <div className="text-danger">{formik.errors.npDays}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Expected Relieve Date */}
                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="expectedRelieveDate">
                        Expected Relieve Date <span className="text-danger">*</span>
                      </Label>
                      <CustomDatePicker
                        name="expectedRelieveDate"
                        value={formik.values.expectedRelieveDate}
                        onChange={(date) => formik.setFieldValue("expectedRelieveDate", date)}
                        onBlur={() => formik.setFieldTouched("expectedRelieveDate", true)}
                        minDate={formik.values.acceptedDate || null}
                        disabled
                      />
                      {formik.touched.expectedRelieveDate && formik.errors.expectedRelieveDate && (
                        <div className="text-danger">{formik.errors.expectedRelieveDate}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* Relieve Date */}
                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="relieveDate">
                        Relieve Date <span className="text-danger">*</span>
                      </Label>
                      <CustomDatePicker
                        name="relieveDate"
                        value={formik.values.relieveDate}
                        disablePast={true}
                        onChange={(date) => formik.setFieldValue("relieveDate", date)}
                        onBlur={() => formik.setFieldTouched("relieveDate", true)}
                        minDate={formik.values.expectedRelieveDate || null} // ✅ updated condition
                      />
                      {formik.touched.relieveDate && formik.errors.relieveDate && (
                        <div className="text-danger">{formik.errors.relieveDate}</div>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4" lg="3">
                    <FormGroup>
                      <Label for="reason">Reason <span className="text-danger">*</span></Label>
                      <Input
                        id="reason"
                        name="reason"
                        type="textarea"
                        maxLength={200}
                        value={formik.values.reason}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        style={{ maxHeight: "33.1px", minHeight: "unset" }}
                        disabled={rejoinId !== null} // Added here
                        // className={
                        //   formik.touched.reason && formik.errors.reason ? "is-invalid" : ""
                        // }
                      />
                      {formik.touched.reason && formik.errors.reason && (
                        <div className="text-danger">{formik.errors.reason}</div>
                      )}
                    </FormGroup>
                  </Col>

                  {/* <Col md="12" className="text-end mob-center">
                    <Button type="submit" className="btn button-save me-2">
                      <i className="fas fa-save me-1" /> {isEdit || rejoinId ? "Update" : "Save"}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleCancel}
                      className="btn button-cancel"> <i className="fas fa-sync me-1" /> Cancel
                    </Button>
                  </Col> */}

                  <Col md="12" className="text-end mob-center">

                    {/* Save / Update Button */}
                    {(canCreate || canEdit) && (
                      <Button type="submit" className="btn button-save me-2">
                        <i className="fas fa-save me-1" />
                        {isEdit || rejoinId ? "Update" : "Save"}
                      </Button>
                    )}

                    {/* Cancel Visible Only if ANY Permission */}
                    {(canCreate || canEdit || canRead) && (
                      <Button
                        type="button"
                        onClick={handleCancel}
                        className="btn button-cancel"
                      >
                        <i className="fas fa-sync me-1" /> Cancel
                      </Button>
                    )}

                  </Col>

                </Row>
              </Form>

            </CardBody>
          </Card>

          {/* Table Section */}
          <Card>
            <CardBody>
              <TableContainer
                columns={columns}
                data={resignationList}
                isGlobalFilter={true}
                customPageSize={10}
              />
            </CardBody>
          </Card>
        </Container>

        {/* Rejoin Modal */}
        <Modal isOpen={isRejoinModalOpen} toggle={toggleRejoinModal}>
          <ModalHeader toggle={toggleRejoinModal}>Enter the Reason</ModalHeader>
          <ModalBody>
            <Input
              type="textarea"
              value={rejoinReason}
              onChange={(e) => setRejoinReason(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button type="submit" className="btn button-save  me-2" onClick={handleRejoinSubmit}>
              <i className="fas fa-save me-1" /> OK
            </Button>
            <Button
              type="button" className="btn button-cancel" onClick={toggleRejoinModal}> <i className="fas fa-sync me-1" /> Cancel
            </Button>
            
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default EmployeeResignation;
